// src/Contexts/MapContext.js

import React, { createContext, useContext, useReducer, useCallback } from 'react';

// --- MODES ---
// This prevents logic conflicts. You can't be "Drawing a Line" and "Editing a Marker" at the same time.
export const MAP_MODES = {
  VIEW: 'VIEW', // Standard browsing
  CREATE_MARKER: 'CREATE_MARKER', // Placing a new pin
  EDIT_MARKER: 'EDIT_MARKER', // Moving/Changing a pin
  DRAW_POLY: 'DRAW_POLY', // Drawing shapes
  EDIT_POLY: 'EDIT_POLY', // Editing shapes
};

// --- INITIAL STATE ---
const initialState = {
  mode: MAP_MODES.VIEW,
  selectedFeature: null, // The raw data object (marker/polygon)
  visualDraft: null, // The visual override (e.g., "Force Orange Pin")
};

// --- REDUCER ---
const mapReducer = (state, action) => {
  switch (action.type) {
    case 'SET_MODE':
      return {
        ...state,
        mode: action.payload,
        // If switching to VIEW, clear selection unless told otherwise
        selectedFeature: action.payload === MAP_MODES.VIEW ? null : state.selectedFeature,
        visualDraft: action.payload === MAP_MODES.VIEW ? null : state.visualDraft,
      };

    case 'SELECT_FEATURE':
      return {
        ...state,
        selectedFeature: action.payload,
      };

    case 'START_CREATION':
      return {
        ...state,
        mode: MAP_MODES.CREATE_MARKER,
        selectedFeature: action.payload.data,
        visualDraft: action.payload.visuals,
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
};

// --- PROVIDER ---
const MapContext = createContext();

export const MapProvider = ({ children }) => {
  const [state, dispatch] = useReducer(mapReducer, initialState);

  const setMode = useCallback(
    (mode) => dispatch({ type: 'SET_MODE', payload: mode }),
    [],
  );
  const selectFeature = useCallback(
    (feature) => dispatch({ type: 'SELECT_FEATURE', payload: feature }),
    [],
  );
  const resetMap = useCallback(() => dispatch({ type: 'RESET' }), []);

  // This is the key action for your "Search Conversion" feature
  const startCreatingMarker = useCallback((data, visualOverrides) => {
    dispatch({
      type: 'START_CREATION',
      payload: { data, visuals: visualOverrides },
    });
  }, []);

  const value = {
    ...state,
    setMode,
    selectFeature,
    resetMap,
    startCreatingMarker,
  };

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
};

export const useMapState = () => {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useMapState must be used within a MapProvider');
  }
  return context;
};
